import { App_Url_New, LOB_IDS } from "@/shared/common/static";
import { axiosInstance } from "@/shared/lib/axiosInstance";
import {
  buildLifeIssuePolicyPayload,
  buildLifeSelectedQuotePayload,
} from "../utils/lifeQuotePayloadsUtils";

const DEFAULT_PAYMENT_FAILURE_MESSAGE =
  "Payment failed. Please check your card details and try again.";

function normalizePaymentStatus(paymentResponse: any): string {
  const directStatus =
    paymentResponse?.payment_status ?? paymentResponse?.paymentStatus ?? paymentResponse?.status;
  const nestedStatus =
    paymentResponse?.data?.payment_status ??
    paymentResponse?.data?.paymentStatus ??
    paymentResponse?.data?.status;

  return String(directStatus ?? nestedStatus ?? "")
    .trim()
    .toLowerCase();
}

function getPaymentFailureReason(paymentResponse: any): string {
  return (
    paymentResponse?.failure_reason ||
    paymentResponse?.failureReason ||
    paymentResponse?.data?.failure_reason ||
    paymentResponse?.data?.failureReason ||
    DEFAULT_PAYMENT_FAILURE_MESSAGE
  );
}

function getApiErrorMessage(error: any, fallback: string): string {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

function normalizeLifeSelectedQuotePayload(payload: Record<string, unknown>) {
  const selectedQuotePayload = payload as {
    selected_quote?: unknown;
    insurer_name?: unknown;
    plan_name?: unknown;
    insurer?: unknown;
    plan?: unknown;
  };

  const candidate =
    selectedQuotePayload?.selected_quote && typeof selectedQuotePayload.selected_quote === "object"
      ? selectedQuotePayload.selected_quote
      : selectedQuotePayload;

  if (!candidate || typeof candidate !== "object") {
    return payload;
  }

  const normalizedCandidate = candidate as {
    insurer_name?: unknown;
    plan_name?: unknown;
    insurer?: unknown;
    plan?: unknown;
  };

  if (normalizedCandidate.insurer_name || normalizedCandidate.plan_name) {
    return candidate as Record<string, unknown>;
  }

  if (normalizedCandidate.insurer || normalizedCandidate.plan) {
    return buildLifeSelectedQuotePayload(normalizedCandidate as any) as Record<string, unknown>;
  }

  return payload;
}

function normalizeLifeUpdatePayload(step: string, payload: Record<string, unknown>) {
  if (step !== "selected_quote") {
    return payload;
  }

  return normalizeLifeSelectedQuotePayload(payload);
}

export const createLifeSession = async (payload: Record<string, unknown>) => {
  const response = await axiosInstance.post(App_Url_New.ApiEndPoint.createSession, payload);
  return response.data;
};

export async function updateLifeSessionStep(params: {
  step: string;
  payload: Record<string, unknown>;
  clientId: string | number;
  sessionId: string | number;
  lobId: number;
  startDate?: string | null;
  endDate?: string | null;
}) {
  const { step, payload, clientId, sessionId, lobId, startDate, endDate } = params;
  const normalizedPayload = normalizeLifeUpdatePayload(step, payload);
  const lifeInsurancePayload =
    normalizedPayload && typeof normalizedPayload === "object"
      ? (normalizedPayload as { life_insurance?: Record<string, unknown> }).life_insurance
      : undefined;

  const derivedStartDate =
    step === "step_2_pre_request"
      ? ((lifeInsurancePayload?.start_date as string | null | undefined) ??
        (lifeInsurancePayload?.policy_dates as { start_date?: string | null } | undefined)
          ?.start_date)
      : undefined;

  const derivedEndDate =
    step === "step_2_pre_request"
      ? ((lifeInsurancePayload?.end_date as string | null | undefined) ??
        (lifeInsurancePayload?.policy_dates as { end_date?: string | null } | undefined)?.end_date)
      : undefined;

  const resolvedStartDate = startDate ?? derivedStartDate;
  const resolvedEndDate = endDate ?? derivedEndDate;

  return axiosInstance.post(App_Url_New.ApiEndPoint.updateSession, {
    step,
    payload: normalizedPayload,
    client_id: Number(clientId),
    session_id: Number(sessionId),
    lob_id: lobId,
    ...(resolvedStartDate !== undefined ? { start_date: resolvedStartDate } : {}),
    ...(resolvedEndDate !== undefined ? { end_date: resolvedEndDate } : {}),
  });
}

export async function submitLifePayment(payload: Record<string, unknown>) {
  const response = await axiosInstance.post(App_Url_New.ApiEndPoint.paymentGateway, payload);
  return response.data;
}

export async function issueLifePolicy(payload: Record<string, unknown>) {
  const response = await axiosInstance.post(App_Url_New.ApiEndPoint.lifeGateway, payload);
  return response.data;
}

export type LifePaymentFlowResult =
  | {
      status: "SUCCESS";
      paymentResponse: any;
      issuePolicyResponse: any;
      policyNumber: string;
      quoteNo: string;
      apiStatus: string;
    }
  | {
      status: "FAILED";
      paymentResponse?: any;
      failureReason: string;
    }
  | {
      status: "SUCCESS_POLICY_FAILED";
      paymentResponse: any;
      failureReason: string;
    };

export async function runLifePaymentFlow(params: {
  selectedQuote: any;
  clientId: string | number | null;
  sessionId: string | number | null;
  paymentPayload: Record<string, unknown>;
}): Promise<LifePaymentFlowResult> {
  const { selectedQuote, clientId, sessionId, paymentPayload } = params;

  try {
    const paymentResponse = await submitLifePayment(paymentPayload);
    const normalizedPaymentStatus = normalizePaymentStatus(paymentResponse);

    if (normalizedPaymentStatus !== "success") {
      return {
        status: "FAILED",
        paymentResponse,
        failureReason: getPaymentFailureReason(paymentResponse),
      };
    }

    const issuePolicyPayload = buildLifeIssuePolicyPayload({
      selectedQuote,
      clientId,
      sessionId,
      paymentResponse,
      lobId: LOB_IDS.LIFE,
    });

    try {
      const issuePolicyResponse = await issueLifePolicy(issuePolicyPayload);
      const apiResponse = issuePolicyResponse?.response;

      return {
        status: "SUCCESS",
        paymentResponse,
        issuePolicyResponse,
        policyNumber: apiResponse?.policyNo || issuePolicyResponse?.policyNo || "",
        quoteNo: issuePolicyResponse?.quoteNo || "",
        apiStatus:
          apiResponse?.status || issuePolicyResponse?.status || "Policy Issued Successfully",
      };
    } catch (issueError: any) {
      return {
        status: "SUCCESS_POLICY_FAILED",
        paymentResponse,
        failureReason: getApiErrorMessage(
          issueError,
          "Payment was successful, but policy generation failed. Please contact support."
        ),
      };
    }
  } catch (paymentError: any) {
    return {
      status: "FAILED",
      failureReason: getApiErrorMessage(paymentError, DEFAULT_PAYMENT_FAILURE_MESSAGE),
    };
  }
}
